import { Formik } from 'formik';
import React, {
  FunctionComponent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { Button, Form, Input, Modal } from 'semantic-ui-react';
import Label from 'components/label/label';
import { ToastError, ToastSuccess } from 'utils';
import DocumentSendFormModalValidationSchema from './document-send-form-modal-validation-schema';
import DocumentSendFormModalView from './document-send-form-modal-view';

export interface DocumentSendFormAdditionalFields {
  givenName: boolean;
  familyName: boolean;
}

export type DocumentSendFormModalFields = {
  telephone: string;
  proReceiptEmail: string;
  givenName?: string;
  familyName?: string;
};

type DocumentSendFormModalContainerProps = {
  open: boolean;
  onSubmitDocument?: (
    values: DocumentSendFormModalFields,
  ) => Promise<{ data: { success: boolean; message: string; link?: string } }>;
  onClose: () => void;
  receiptEmailSuggestions?: string[];
  proPersonSuggestions?: {
    givenName: string;
    familyName: string;
    telephone: string;
  }[];
  additionalFields?: DocumentSendFormAdditionalFields;
  documentType?: string;
};

const DocumentSendFormModalContainer: FunctionComponent<
  DocumentSendFormModalContainerProps
> = ({
  onSubmitDocument,
  receiptEmailSuggestions,
  proPersonSuggestions,
  onClose,
  additionalFields,
  documentType = 'document',
  ...rest
}) => {
  const [linkModal, setLinkModal] = useState<{
    open: boolean;
    link?: string;
  }>({ open: false });

  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = link;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, []);

  const handleOpenLink = useCallback((link: string) => {
    window.open(link, '_blank', 'noopener,noreferrer');
  }, []);

  const onSubmit = useCallback(
    async (values: DocumentSendFormModalFields) => {
      if (onSubmitDocument) {
        const response = await onSubmitDocument(values);

        if (response.data?.success === true) {
          if (response.data.link && response.data.link.trim() !== '') {
            onClose();
            setLinkModal({
              open: true,
              link: response.data.link,
            });
            ToastSuccess(
              'Succès',
              'Le document a bien été envoyé pour signature.',
            );
          } else {
            ToastSuccess(
              'Succès',
              'Le document a bien été envoyé pour signature. Un lien de signature a été envoyé par SMS.',
            );
            onClose();
          }
        } else {
          ToastError('Erreur', response.data?.message);
        }
      }
    },
    [onSubmitDocument, onClose],
  );

  const initialValues = useMemo(() => {
    const givenName = additionalFields?.givenName
      ? {
          givenName: proPersonSuggestions?.[0]?.givenName || '',
        }
      : {};

    const familyName = additionalFields?.familyName
      ? {
          familyName: proPersonSuggestions?.[0]?.familyName || '',
        }
      : {};

    return {
      ...givenName,
      ...familyName,
      proReceiptEmail: receiptEmailSuggestions?.[0] || '',
      telephone: proPersonSuggestions?.[0]?.telephone || '',
    };
  }, [receiptEmailSuggestions, proPersonSuggestions]);

  return (
    <>
      <Formik
        onSubmit={onSubmit}
        initialValues={initialValues}
        enableReinitialize
        validationSchema={DocumentSendFormModalValidationSchema(
          additionalFields,
        )}
        validateOnChange={true}
      >
        <DocumentSendFormModalView
          onClose={onClose}
          additionalFields={additionalFields}
          receiptEmailSuggestions={receiptEmailSuggestions}
          proPersonSuggestions={proPersonSuggestions}
          {...rest}
        />
      </Formik>

      {/* Modal pour afficher le lien de signature */}
      <Modal
        open={linkModal.open}
        onClose={() => setLinkModal({ open: false })}
        size="small"
      >
        <Modal.Header>
          {documentType
            ? documentType.charAt(0).toUpperCase() + documentType.slice(1)
            : 'Document'}{' '}
          envoyé avec succès !
        </Modal.Header>
        <Modal.Content>
          <p>
            Le {documentType || 'document'} a été envoyé par SMS. En cas de
            soucis, vous pouvez également accéder directement au lien de
            signature ci-dessous :
          </p>{' '}
          <div style={{ marginTop: '20px', marginBottom: '20px' }}>
            <Form.Field>
              <Label>Lien de signature :</Label>
              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'flex-start',
                }}
              >
                <Input
                  fluid
                  value={linkModal.link || ''}
                  readOnly
                  style={{ flex: 1, minHeight: '36px' }}
                />
                <Button
                  icon="copy"
                  color={copied ? 'green' : 'blue'}
                  onClick={() => linkModal.link && handleCopy(linkModal.link)}
                  content={copied ? 'Copié !' : 'Copier'}
                />
                <Button
                  icon="external alternate"
                  onClick={() =>
                    linkModal.link && handleOpenLink(linkModal.link)
                  }
                  content="Ouvrir le lien"
                />
              </div>
            </Form.Field>
          </div>
          <div
            style={{
              padding: '12px',
              backgroundColor: '#f8f9fa',
              borderRadius: '4px',
              border: '1px solid #e9ecef',
              marginBottom: '20px',
            }}
          >
            <strong>Note :</strong> Ce lien est valable pendant 48 heures.
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setLinkModal({ open: false })}>Fermer</Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default DocumentSendFormModalContainer;
